import { Flask } from '@phosphor-icons/react';
import { endOfDay, startOfDay, subDays } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from 'src/components/Button';
import { Modal } from 'src/components/Modal';
import { TranscriptDisplay } from 'src/components/TranscriptDisplay';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Agent } from 'src/interfaces/agent.interface';
import { Call, CallFilters, getAgentId } from 'src/interfaces/call.interface';
import { QueryObject } from 'src/interfaces/queryObject.interface';
import { parseTranscript } from 'src/utils/transcript';
import { CallSelectorFilters } from './CallSelectorFilters';
import { CallSelectorTable } from './CallSelectorTable';

export type CallSelectorState = {
  filters: CallFilters;
  currentPage: number;
  sortConfig: { key: string | null; direction: string | null };
  selectedCall: Call | null;
};

type CallSelectorViewModalProps = {
  isOpen: boolean;
  onSelectCall: (call: Call, state: CallSelectorState) => void;
  onCancel: () => void;
  onClose: () => void;
  initialState?: CallSelectorState;
  defaultAgent?: Agent;
};

const agentNameCache: Record<string, string> = {};

const getDefaultFilters = (defaultAgentId?: string): CallFilters => {
  const end = endOfDay(new Date());
  const start = startOfDay(subDays(end, 27));
  return {
    agent: defaultAgentId || 'All Agents',
    call_id: '',
    start_time: {
      comparison: 'btwn',
      value: [start.toISOString(), end.toISOString()],
    },
    transcript_keyword: '',
  };
};

export const CallSelectorViewModal = ({
  isOpen,
  onSelectCall,
  onCancel,
  onClose,
  initialState,
  defaultAgent,
}: CallSelectorViewModalProps) => {
  const { get } = useAuthedRequest();

  const [agents, setAgents] = useState<Agent[]>(() =>
    defaultAgent ? [defaultAgent] : [],
  );
  const [calls, setCalls] = useState<Call[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(
    initialState?.currentPage || 1,
  );
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize] = useState(5);

  const [selectedCall, setSelectedCall] = useState<Call | null>(
    initialState?.selectedCall || null,
  );

  const [filters, setFilters] = useState<CallFilters>(
    initialState?.filters || getDefaultFilters(defaultAgent?.id),
  );

  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: string | null;
  }>(initialState?.sortConfig || { key: 'start_time', direction: 'desc' });

  const fetchCalls = useCallback(
    async (page: number, size: number) => {
      setLoading(true);

      const formattedFilters: CallFilters = {
        ...filters,
        ...(filters.agent === 'All Agents' && { agent: undefined }),
        ...(filters.call_id?.trim() === '' && { call_id: undefined }),
        ...(filters.transcript_keyword?.trim() === '' && {
          transcript_keyword: undefined,
        }),
        ...(filters.start_time === null && { start_time: undefined }),
      };

      const queryObject: QueryObject = {
        page: String(page),
        size: String(size),
        filters: JSON.stringify(formattedFilters),
      };

      if (sortConfig.key) {
        queryObject.sort_column = sortConfig.key;
        queryObject.sort_desc =
          sortConfig.direction === 'desc' ? 'true' : 'false';
      }

      const query = new URLSearchParams(queryObject as Record<string, string>);

      try {
        const fetchResult = await get(`/calls?${query.toString()}`);
        setCalls(fetchResult.items);
        setTotalItems(fetchResult.total);

        // Fetch agent names for calls
        const uniqueAgents: string[] = Array.from(
          new Set(
            fetchResult.items
              .map((call: Call) => getAgentId(call.agent))
              .filter((agentId: string | null): agentId is string => !!agentId),
          ),
        );

        await Promise.all(
          uniqueAgents.map(async (uniqueAgent) => {
            if (!agentNameCache[uniqueAgent]) {
              try {
                const agent = await get(`/agents/${uniqueAgent}`);
                agentNameCache[uniqueAgent] = agent.name;
              } catch {
                agentNameCache[uniqueAgent] = '(deleted)';
              }
            }
          }),
        );
      } catch (error) {
        console.error('Error fetching calls:', error);
      } finally {
        setLoading(false);
      }
    },
    [filters, sortConfig, get],
  );

  const searchAgents = useCallback(
    async (name: string) => {
      const queryObject: QueryObject = {
        ...(name.length !== 0 && { filters: JSON.stringify({ name }) }),
      };
      const query = new URLSearchParams(queryObject as Record<string, string>);

      const data = await get(`/agents?${query.toString()}`);
      setAgents((prev) => {
        const nextById = new Map<string, Agent>();
        prev.forEach((a) => a.id && nextById.set(a.id, a));
        data.items.forEach((a: Agent) => a.id && nextById.set(a.id, a));
        return Array.from(nextById.values());
      });

      return data.items.map((agent: Agent) => ({
        value: agent.id,
        label: agent.name,
      }));
    },
    [get],
  );

  useEffect(() => {
    if (isOpen) {
      fetchCalls(currentPage, pageSize);
    }
  }, [isOpen, currentPage, pageSize, filters, sortConfig, fetchCalls]);

  const handleFiltersChange = (newFilters: CallFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSort = (key: string) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleSelectCallFromTable = (call: Call) => {
    setSelectedCall(call);
  };

  const handleConfirmSelection = () => {
    if (!selectedCall) return;

    const currentState: CallSelectorState = {
      filters,
      currentPage,
      sortConfig,
      selectedCall,
    };

    onSelectCall(selectedCall, currentState);
  };

  const transcriptMessages = useMemo(() => {
    if (!selectedCall?.transcript) return [];
    return parseTranscript(selectedCall.transcript);
  }, [selectedCall]);

  const selectedCallAgentName = useMemo(() => {
    if (!selectedCall) return '';
    const agentId = getAgentId(selectedCall.agent);
    return agentId ? agentNameCache[agentId] || '(unknown)' : '(deleted)';
  }, [selectedCall]);

  const renderFooter = () => (
    <div className="flex justify-end gap-4 pt-4 px-6 pb-6 flex-shrink-0 border-t border-neutral-200">
      <Button variant="outlined" onClick={onCancel} size="small">
        Cancel
      </Button>
      <Button
        onClick={handleConfirmSelection}
        disabled={!selectedCall}
        size="small"
      >
        <Flask className="h-4 w-4 mr-2" />
        Confirm Selection
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      showClose
      showBack
      onBack={onCancel}
      title="Select a Call"
      subtitle="Choose a call to test this insight on."
      footer={renderFooter()}
      showModalBorder
      className="lg:max-w-7xl h-[90vh]"
    >
      <div className="flex h-full overflow-hidden -mx-6">
        {/* Left: Call List */}
        <div className="w-3/5 flex flex-col border-r border-neutral-200 min-h-0 overflow-hidden">
          <CallSelectorFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
            agents={agents}
            searchAgents={searchAgents}
          />

          <div className="flex-1 overflow-auto p-4">
            <CallSelectorTable
              calls={calls}
              agentNames={agentNameCache}
              selectedCallId={selectedCall?.id || null}
              onSelectCall={handleSelectCallFromTable}
              totalItems={totalItems}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              onSort={handleSort}
              loading={loading}
              pageSize={pageSize}
            />
          </div>
        </div>

        {/* Right: Transcript Preview */}
        <div className="w-2/5 flex flex-col min-h-0 overflow-hidden">
          <div className="p-4 flex-shrink-0">
            <h3 className="font-medium text-sm text-neutral-700">Transcript</h3>
            {selectedCall && (
              <p className="text-xs text-neutral-400 mt-1">
                {selectedCall.id.slice(0, 8)}... • {selectedCallAgentName}
              </p>
            )}
          </div>
          <div className="flex-1 min-h-0 overflow-auto px-4 pb-4">
            {selectedCall ? (
              <TranscriptDisplay
                messages={transcriptMessages}
                rawTranscript={selectedCall.transcript}
                className="h-full"
                emptyStateMessage="No transcript available for this call."
              />
            ) : (
              <div className="flex items-center justify-center h-full text-neutral-400 text-sm">
                Select a call to view its transcript
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
